/**
 * マッチングロジック v3 (部制対応)
 * モード:
 *   cast_select  : キャストがゲストを指名
 *   guest_select : ゲストがキャストを指名
 *   both_select  : 双方の希望スコアで決定
 *   random_only  : 完全ランダム
 *
 * 部制(sessions)が設定されている場合:
 *   - キャストはsession_attendanceに基づき「部スロット」に展開される
 *   - 1部につき1ゲストとマッチ、1ゲストは1部のみ当選
 *   - raffle/openは部ごとに定員分抽選し、前の部から詰める
 */

// ===========================
// ユーティリティ
// ===========================

/** Fisher-Yates shuffle */
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/** Firebase が配列をオブジェクトに変換する場合に対応 */
const normalizePicks = (draft_picks) => {
  if (!draft_picks) return [];
  if (Array.isArray(draft_picks)) return draft_picks;
  return Object.keys(draft_picks)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => draft_picks[k]);
};

/**
 * 「最低」(priority < 0) の要素を必ず末尾へ回す安定パーティション。
 * 通常(0)・優先(>=1)の順序はそのまま保持するので、ランダム性は壊さない。
 * 枠が足りない時に最初にあぶれるのが「最低」になる。
 * @param {Array}    arr     並べ替え対象（id または {priority} オブジェクト）
 * @param {Function} prioOf  各要素の優先度を返す関数
 */
const lowestLast = (arr, prioOf) => [
  ...arr.filter((x) => (prioOf(x) || 0) >= 0),
  ...arr.filter((x) => (prioOf(x) || 0) < 0),
];


// ===========================
// ドラフト方式（共通）
// ===========================

/**
 * @param {Object} proposersDict  指名する側 { id: { priority, draft_picks, ... } }
 * @param {Object} targetsDict    指名される側
 * @returns {Array} [{ proposer_id, target_id, round, match_type }]
 */
const matchDraft = (proposersDict, targetsDict) => {
  const pairs = [];
  const assignedProposers = new Set();
  const assignedTargets = new Set();
  const availableTargets = new Set(Object.keys(targetsDict));

  const proposers = Object.entries(proposersDict).map(([id, data]) => ({
    id,
    priority: data.priority || 0,
    draft_picks: normalizePicks(data.draft_picks),
  }));

  // ===== 優先度保証フェーズ（priority >= 1） =====
  const priorityProposers = shuffle(proposers.filter((p) => p.priority >= 1));
  priorityProposers.forEach((p) => {
    if (assignedProposers.has(p.id)) return;
    const firstPick = p.draft_picks[0];
    if (!firstPick || !availableTargets.has(firstPick) || assignedTargets.has(firstPick)) return;

    pairs.push({ proposer_id: p.id, target_id: firstPick, round: 1, match_type: 'draft' });
    assignedProposers.add(p.id);
    assignedTargets.add(firstPick);
  });

  // ===== ドラフトラウンド =====
  const maxRounds = proposers.reduce((m, p) => Math.max(m, p.draft_picks.length), 0);

  for (let round = 0; round < maxRounds; round++) {
    const roundCandidates = {};
    proposers.forEach((p) => {
      if (assignedProposers.has(p.id)) return;
      const targetId = p.draft_picks[round];
      if (!targetId || !availableTargets.has(targetId) || assignedTargets.has(targetId)) return;
      if (!roundCandidates[targetId]) roundCandidates[targetId] = [];
      roundCandidates[targetId].push(p);
    });

    Object.entries(roundCandidates).forEach(([targetId, candidates]) => {
      const sorted = shuffle(candidates).sort((a, b) => b.priority - a.priority);
      const winner = sorted[0];
      pairs.push({ proposer_id: winner.id, target_id: targetId, round: round + 1, match_type: 'draft' });
      assignedProposers.add(winner.id);
      assignedTargets.add(targetId);
    });

    if (
      assignedProposers.size === proposers.length ||
      assignedTargets.size === availableTargets.size
    ) break;
  }

  // ===== ランダム割当フェーズ =====
  // ランダムに割り当てるが、提案者・対象の両側で「最低」(priority < 0) を
  // 末尾へ回す。枠が余った時だけ最低が当たる（通常/優先は従来どおりランダム）。
  const unassignedTargets = lowestLast(
    shuffle([...availableTargets].filter((id) => !assignedTargets.has(id))),
    (id) => targetsDict[id]?.priority
  );
  const unassignedProposers = lowestLast(
    shuffle(proposers.filter((p) => !assignedProposers.has(p.id))),
    (p) => p.priority
  );
  unassignedProposers.forEach((p, i) => {
    if (i >= unassignedTargets.length) return;
    pairs.push({ proposer_id: p.id, target_id: unassignedTargets[i], round: 0, match_type: 'random' });
  });

  return pairs;
};

// ===========================
// 非セッション版モード別マッチング
// ===========================

const matchByCast = (casts, guests) =>
  matchDraft(casts, guests).map((p) => ({
    cast_id: p.proposer_id, guest_id: p.target_id, round: p.round, match_type: p.match_type,
  }));

const matchByGuest = (casts, guests) =>
  matchDraft(guests, casts).map((p) => ({
    cast_id: p.target_id, guest_id: p.proposer_id, round: p.round, match_type: p.match_type,
  }));

const matchByBoth = (casts, guests) => {
  const assignedCasts  = new Set();
  const assignedGuests = new Set();
  const pairs = [];

  const castList = Object.entries(casts).map(([id, data]) => ({
    id, priority: data.priority || 0, draft_picks: normalizePicks(data.draft_picks),
  }));
  const guestList = Object.entries(guests).map(([id, data]) => ({
    id, priority: data.priority || 0, draft_picks: normalizePicks(data.draft_picks),
  }));

  shuffle(castList.filter((c) => c.priority >= 1)).forEach((cast) => {
    if (assignedCasts.has(cast.id)) return;
    const firstPick = cast.draft_picks[0];
    if (!firstPick || assignedGuests.has(firstPick)) return;
    pairs.push({ cast_id: cast.id, guest_id: firstPick, round: 1, match_type: 'draft' });
    assignedCasts.add(cast.id);
    assignedGuests.add(firstPick);
  });

  const getScore = (picks, targetId) => {
    if (!picks || picks.length === 0) return 0;
    const index = picks.indexOf(targetId);
    return index === -1 ? 0 : Math.max(10 - index, 1);
  };

  const pairScores = [];
  castList.forEach((cast) => {
    if (assignedCasts.has(cast.id)) return;
    guestList.forEach((guest) => {
      if (assignedGuests.has(guest.id)) return;
      const rawScore = getScore(cast.draft_picks, guest.id) + getScore(guest.draft_picks, cast.id);
      const lowPenalty = (guest.priority || 0) < 0 ? -1000 : 0; // 「最低」は最後尾へ
      pairScores.push({ cast_id: cast.id, guest_id: guest.id, rawScore, score: rawScore + lowPenalty + Math.random() * 0.5 });
    });
  });

  pairScores.sort((a, b) => b.score - a.score);
  pairScores.forEach(({ cast_id, guest_id, rawScore }) => {
    if (assignedCasts.has(cast_id) || assignedGuests.has(guest_id)) return;
    pairs.push({ cast_id, guest_id, round: rawScore > 0 ? 1 : 0, match_type: rawScore > 0 ? 'draft' : 'random' });
    assignedCasts.add(cast_id);
    assignedGuests.add(guest_id);
  });

  return pairs;
};

const matchByRandom = (casts, guests) => {
  const castIds  = shuffle(Object.keys(casts));
  // 「最低」のゲストは枠が余った時だけ当たるよう末尾へ回す
  const guestIds = lowestLast(shuffle(Object.keys(guests)), (id) => guests[id]?.priority);
  return castIds.map((cast_id, i) => ({
    cast_id, guest_id: guestIds[i] ?? null, round: 0, match_type: 'random',
  })).filter(p => p.guest_id !== null);
};

// ===========================
// スロット操作ユーティリティ
// ===========================

const SLOT_SEP = '__ses__';
const makeSlotId   = (castId, ses) => `${castId}${SLOT_SEP}${ses}`;
const parseSlotId  = (slotId) => {
  const idx = slotId.lastIndexOf(SLOT_SEP);
  return { castId: slotId.slice(0, idx), session: parseInt(slotId.slice(idx + SLOT_SEP.length), 10) };
};

/**
 * キャストを「キャストID__ses__部番号」のスロットに展開する。
 * session_attendance で working の部のみ。未設定なら全部対象。
 * numGuests を渡すと「その人数を満たす最小の部数まで」に制限して
 * 余分な後ろの部が使われないようにする（前詰め保証）。
 */
const buildCastSlots = (casts, sessionAttToday, sessions, numGuests) => {
  // ゲスト数が指定されている場合: 1部〜N部の累積キャパシティが numGuests を
  // 超えた時点で打ち切り、余分なスロット（より後の部）を露出しない
  let effectiveSessions = sessions;
  if (numGuests > 0) {
    let capacity = 0;
    for (let ses = 1; ses <= sessions; ses++) {
      const sesCount = Object.keys(casts).filter(castId => {
        const att = sessionAttToday?.[castId] || {};
        const hasAnyWorking = Object.values(att).some(s => s === 'working');
        return !hasAnyWorking || att[ses] === 'working';
      }).length;
      capacity += sesCount;
      if (capacity >= numGuests) { effectiveSessions = ses; break; }
    }
  }

  const slots = {};
  Object.entries(casts).forEach(([castId, cast]) => {
    const att = sessionAttToday?.[castId] || {};
    const hasAnyWorking = Object.values(att).some(s => s === 'working');
    for (let ses = 1; ses <= effectiveSessions; ses++) {
      if (!hasAnyWorking || att[ses] === 'working') {
        slots[makeSlotId(castId, ses)] = cast;
      }
    }
  });
  return slots;
};

/**
 * ゲストの指名（castId配列）をスロットIDに展開する。
 * [castA, castB] → [castA__ses__1, castA__ses__2, castB__ses__1, ...]
 */
const expandPicksToSlots = (picks, availableSlotIds) => {
  const expanded = [];
  picks.forEach(castId => {
    availableSlotIds
      .filter(sid => sid.startsWith(`${castId}${SLOT_SEP}`))
      .sort((a, b) => parseSlotId(a).session - parseSlotId(b).session)
      .forEach(sid => expanded.push(sid));
  });
  return expanded;
};

// ===========================
// 部割り当て前詰め
// ===========================

/**
 * マッチング済みペアに対して、キャストごとに最も早い利用可能な部を割り当てる。
 * スロット方式のマッチング後にこの関数で部番号を正規化する。
 * キャストが複数ゲストとマッチした場合は若い部番号から順に消費する。
 */
const assignSessionsFrontLoaded = (pairs, casts, sessionAttToday, sessions) => {
  const castAvailSessions = {};
  Object.keys(casts).forEach(castId => {
    const att = sessionAttToday?.[castId] || {};
    const hasAnyWorking = Object.values(att).some(s => s === 'working');
    const available = [];
    for (let ses = 1; ses <= sessions; ses++) {
      if (!hasAnyWorking || att[ses] === 'working') available.push(ses);
    }
    castAvailSessions[castId] = available;
  });

  const castSessionsUsed = {};
  return pairs.map(pair => {
    const { cast_id } = pair;
    if (!castSessionsUsed[cast_id]) castSessionsUsed[cast_id] = new Set();
    const used      = castSessionsUsed[cast_id];
    const available = castAvailSessions[cast_id] || [];
    const nextSes   = available.find(ses => !used.has(ses)) ?? pair.session;
    used.add(nextSes);
    return { ...pair, session: nextSes };
  });
};

// ===========================
// セッション対応版モード別マッチング
// フェーズ1: スロット展開 → matchDraft で全体マッチング（指名を正しく処理）
// フェーズ2: assignSessionsFrontLoaded で部番号を前詰めに正規化
// ===========================

/** cast_select: キャストスロットが提案者、ゲストが対象 */
const matchByCastWithSessions = (casts, guests, sessionAttToday, sessions) => {
  const numGuests = Object.keys(guests).length;
  const castSlots = buildCastSlots(casts, sessionAttToday, sessions, numGuests);
  const pairs = matchDraft(castSlots, guests).map(p => {
    const { castId, session } = parseSlotId(p.proposer_id);
    return { cast_id: castId, guest_id: p.target_id, session, round: p.round, match_type: p.match_type };
  });
  return assignSessionsFrontLoaded(pairs, casts, sessionAttToday, sessions);
};

/** guest_select: ゲストが提案者、キャストスロットが対象。ゲストの指名はスロットIDに展開 */
const matchByGuestWithSessions = (casts, guests, sessionAttToday, sessions) => {
  const numGuests = Object.keys(guests).length;
  const castSlots = buildCastSlots(casts, sessionAttToday, sessions, numGuests);
  const slotIds   = Object.keys(castSlots);
  const guestsExpanded = Object.fromEntries(
    Object.entries(guests).map(([gid, guest]) => [
      gid,
      { ...guest, draft_picks: expandPicksToSlots(normalizePicks(guest.draft_picks), slotIds) },
    ])
  );
  const pairs = matchDraft(guestsExpanded, castSlots).map(p => {
    const { castId, session } = parseSlotId(p.target_id);
    return { cast_id: castId, guest_id: p.proposer_id, session, round: p.round, match_type: p.match_type };
  });
  return assignSessionsFrontLoaded(pairs, casts, sessionAttToday, sessions);
};

/** both_select: スロット×ゲストのスコアマトリクス */
const matchByBothWithSessions = (casts, guests, sessionAttToday, sessions) => {
  const numGuests = Object.keys(guests).length;
  const castSlots = buildCastSlots(casts, sessionAttToday, sessions, numGuests);
  const assignedSlots  = new Set();
  const assignedGuests = new Set();
  const pairs = [];

  const slotList = Object.entries(castSlots).map(([slotId, cast]) => ({
    slotId, ...parseSlotId(slotId),
    priority:    cast.priority || 0,
    draft_picks: normalizePicks(cast.draft_picks),
  }));
  const guestList = Object.entries(guests).map(([id, data]) => ({
    id, priority: data.priority || 0, draft_picks: normalizePicks(data.draft_picks),
  }));

  // 優先度保証: castIdごとに1スロット確保
  const priorityCastIds = [...new Set(slotList.filter(s => s.priority >= 1).map(s => s.castId))];
  shuffle(priorityCastIds).forEach(castId => {
    const availSlots = slotList.filter(s => s.castId === castId && !assignedSlots.has(s.slotId));
    if (availSlots.length === 0) return;
    const firstPick = availSlots[0].draft_picks[0];
    if (!firstPick || assignedGuests.has(firstPick)) return;
    const slot = availSlots[0];
    pairs.push({ cast_id: castId, guest_id: firstPick, session: slot.session, round: 1, match_type: 'draft' });
    assignedSlots.add(slot.slotId);
    assignedGuests.add(firstPick);
  });

  const getScore = (picks, targetId) => {
    if (!picks?.length) return 0;
    const i = picks.indexOf(targetId);
    return i === -1 ? 0 : Math.max(10 - i, 1);
  };

  const pairScores = [];
  slotList.forEach(slot => {
    if (assignedSlots.has(slot.slotId)) return;
    guestList.forEach(guest => {
      if (assignedGuests.has(guest.id)) return;
      const rawScore = getScore(slot.draft_picks, guest.id) + getScore(guest.draft_picks, slot.castId);
      const lowPenalty = (guest.priority || 0) < 0 ? -1000 : 0; // 「最低」は最後尾へ
      pairScores.push({ slotId: slot.slotId, castId: slot.castId, session: slot.session, guest_id: guest.id, rawScore, score: rawScore + lowPenalty + Math.random() * 0.5 });
    });
  });

  pairScores.sort((a, b) => b.score - a.score);
  pairScores.forEach(({ slotId, castId, session, guest_id, rawScore }) => {
    if (assignedSlots.has(slotId) || assignedGuests.has(guest_id)) return;
    pairs.push({ cast_id: castId, guest_id, session, round: rawScore > 0 ? 1 : 0, match_type: rawScore > 0 ? 'draft' : 'random' });
    assignedSlots.add(slotId);
    assignedGuests.add(guest_id);
  });

  return assignSessionsFrontLoaded(pairs, casts, sessionAttToday, sessions);
};

/** random_only: スロットとゲストを完全ランダムでペアリング */
const matchByRandomWithSessions = (casts, guests, sessionAttToday, sessions) => {
  const numGuests = Object.keys(guests).length;
  const castSlots = buildCastSlots(casts, sessionAttToday, sessions, numGuests);
  const slotIds   = shuffle(Object.keys(castSlots));
  // 「最低」のゲストは枠が余った時だけ当たるよう末尾へ回す
  const guestIds  = lowestLast(shuffle(Object.keys(guests)), (id) => guests[id]?.priority);
  const pairs = slotIds.map((slotId, i) => {
    if (i >= guestIds.length) return null;
    const { castId, session } = parseSlotId(slotId);
    return { cast_id: castId, guest_id: guestIds[i], session, round: 0, match_type: 'random' };
  }).filter(Boolean);
  return assignSessionsFrontLoaded(pairs, casts, sessionAttToday, sessions);
};

// ===========================
// エントリーポイント
// ===========================

/**
 * cast_instance: 出勤キャストをランダムにインスタンスへ割り振る。
 * instanceCount または instanceCapacity のどちらかで動作モードを切り替え。
 * 部制がある場合は部ごとに独立して割り振る（その部に出勤するキャストのみ対象）。
 *
 * @param {Object} casts
 * @param {number|null} instanceCount    - インスタンス数指定モード
 * @param {number|null} instanceCapacity - 1インスタンスあたりの人数指定モード
 * @param {number|null} sessions
 * @param {Object|null} sessionAttToday
 */
export const runCastInstance = (casts, instanceCount, instanceCapacity, sessions, sessionAttToday) => {
  const resolveCount = (numCasts) => {
    if (instanceCount && instanceCount >= 1) return instanceCount;
    if (instanceCapacity && instanceCapacity >= 1) return Math.max(1, Math.ceil(numCasts / instanceCapacity));
    return 1;
  };

  const isLeader = (castId) => !!casts[castId]?.is_instance_leader;

  /**
   * castIds を count 個のインスタンスへ割り振る。
   * pinned_instance が設定されたキャストは先にそのインスタンスへ固定配置し、
   * 各インスタンスにリーダーを必ず1人ずつ配置する。
   * リーダーが足りなければ insufficient を返す。
   */
  const assign = (castIds) => {
    const count = resolveCount(castIds.length);
    const buckets = Array.from({ length: count }, () => []);

    // pinned_instance が有効範囲（1〜count）のキャストを先に固定配置
    const pinnedIds = castIds.filter((id) => {
      const inst = casts[id]?.pinned_instance;
      return inst != null && inst >= 1 && inst <= count;
    });
    const unpinnedIds = castIds.filter((id) => {
      const inst = casts[id]?.pinned_instance;
      return !(inst != null && inst >= 1 && inst <= count);
    });
    pinnedIds.forEach((id) => buckets[casts[id].pinned_instance - 1].push(id));

    // 固定済みリーダーがいないインスタンスを特定
    const instancesNeedingLeader = [];
    for (let i = 0; i < count; i++) {
      if (!buckets[i].some((id) => isLeader(id))) instancesNeedingLeader.push(i);
    }

    const unpinnedLeaders = shuffle(unpinnedIds.filter(isLeader));
    const unpinnedOthers  = shuffle(unpinnedIds.filter((id) => !isLeader(id)));

    if (unpinnedLeaders.length < instancesNeedingLeader.length) {
      const totalLeaders = pinnedIds.filter(isLeader).length + unpinnedLeaders.length;
      return { insufficient: { need: count, have: totalLeaders } };
    }

    // リーダー未配置のインスタンスに非固定リーダーを1人ずつ割り当て
    instancesNeedingLeader.forEach((instIdx, i) => buckets[instIdx].push(unpinnedLeaders[i]));

    // 残り（余ったリーダー＋その他）を均等に詰める
    const remaining = [...unpinnedLeaders.slice(instancesNeedingLeader.length), ...unpinnedOthers];
    remaining.forEach((id, i) => buckets[i % count].push(id));

    const pairs = [];
    buckets.forEach((ids, idx) => ids.forEach((id) => pairs.push({ cast_id: id, instance_number: idx + 1 })));
    return { pairs };
  };

  if (sessions && sessions >= 2) {
    const allPairs   = [];
    const shortfalls = [];
    for (let ses = 1; ses <= sessions; ses++) {
      const sesCasts = Object.entries(casts).filter(([castId]) => {
        const att = (sessionAttToday || {})[castId] || {};
        const hasAnyWorking = Object.values(att).some(s => s === 'working');
        return !hasAnyWorking || att[ses] === 'working';
      }).map(([id]) => id);
      if (sesCasts.length === 0) continue;
      const r = assign(sesCasts);
      if (r.insufficient) { shortfalls.push({ session: ses, ...r.insufficient }); continue; }
      r.pairs.forEach((p) => allPairs.push({ ...p, session: ses }));
    }
    if (shortfalls.length > 0) return { error: 'insufficient_leaders', shortfalls };
    return { generated_at: Date.now(), mode: 'cast_instance', pairs: allPairs };
  }

  const workingCasts = Object.entries(casts)
    .filter(([, cast]) => cast.status === 'working')
    .map(([id]) => id);
  const r = assign(workingCasts);
  if (r.insufficient) {
    return { error: 'insufficient_leaders', shortfalls: [{ session: null, ...r.insufficient }] };
  }
  return { generated_at: Date.now(), mode: 'cast_instance', pairs: r.pairs };
};

/**
 * open: 登録順（Firebase key の辞書順）で先着確定する。
 * sessions が指定された場合、部ごとに定員分先着し前の部から詰める。
 * 同じゲストが複数部に当選することはない。
 */
export const runOpen = (guests, capacity, sessions) => {
  const allIds = Object.keys(guests).sort();

  if (!sessions) {
    const limit = capacity > 0 ? Math.min(capacity, allIds.length) : allIds.length;
    const group = (p) => allIds.filter(id => (guests[id]?.priority ?? 0) === p);
    const winners = [];
    const fill = (pool) => {
      const slots = limit - winners.length;
      if (slots <= 0 || pool.length === 0) return;
      winners.push(...pool.slice(0, slots));
    };
    const rest = (pred) => allIds.filter(id => !winners.includes(id) && pred(guests[id]?.priority ?? 0));
    fill(group(1)); fill(group(2)); fill(group(3));
    fill(rest(p => p >= 0));   // 通常
    fill(rest(p => p < 0));    // 最低（最後尾）
    const winnerSet = new Set(winners);
    return {
      generated_at: Date.now(), mode: 'open',
      pairs:  winners.map(gid => ({ guest_id: gid, cast_id: null, match_type: (guests[gid]?.priority ?? 0) >= 1 ? 'priority' : 'open' })),
      losers: allIds.filter(id => !winnerSet.has(id)),
    };
  }

  // 部制対応: 部ごとに定員分確保し前の部に詰める
  const perSession = capacity > 0 ? capacity : allIds.length;
  const allPairs   = [];
  const globalWon  = new Set();

  for (let ses = 1; ses <= sessions; ses++) {
    const remaining = allIds.filter(id => !globalWon.has(id));
    if (remaining.length === 0) break;
    const limit = Math.min(perSession, remaining.length);
    const group  = (p) => remaining.filter(id => (guests[id]?.priority ?? 0) === p);
    const sesWinners = [];
    const fill = (pool) => {
      const slots = limit - sesWinners.length;
      if (slots <= 0 || pool.length === 0) return;
      sesWinners.push(...pool.slice(0, slots));
    };
    const rest = (pred) => remaining.filter(id => !sesWinners.includes(id) && pred(guests[id]?.priority ?? 0));
    fill(group(1)); fill(group(2)); fill(group(3));
    fill(rest(p => p >= 0));   // 通常
    fill(rest(p => p < 0));    // 最低（最後尾）
    sesWinners.forEach(gid => {
      globalWon.add(gid);
      allPairs.push({ guest_id: gid, session: ses, cast_id: null, match_type: (guests[gid]?.priority ?? 0) >= 1 ? 'priority' : 'open' });
    });
  }

  return {
    generated_at: Date.now(), mode: 'open',
    pairs:  allPairs,
    losers: allIds.filter(id => !globalWon.has(id)),
  };
};

/**
 * raffle: 優先度順に枠を確保してから残り枠をランダム補充。
 * sessions が指定された場合、部ごとに定員分抽選し前の部から詰める。
 * 同じゲストが複数部に当選することはない。
 */
export const runRaffle = (guests, capacity, sessions) => {
  const allIds = Object.keys(guests);

  if (!sessions) {
    const limit = capacity > 0 ? Math.min(capacity, allIds.length) : allIds.length;
    const group  = (p) => shuffle(allIds.filter(id => (guests[id]?.priority ?? 0) === p));
    const winners = [];
    const fill = (pool) => {
      const slots = limit - winners.length;
      if (slots <= 0 || pool.length === 0) return;
      winners.push(...pool.slice(0, slots));
    };
    const rest = (pred) => shuffle(allIds.filter(id => !winners.includes(id) && pred(guests[id]?.priority ?? 0)));
    fill(group(1)); fill(group(2)); fill(group(3));
    fill(rest(p => p >= 0));   // 通常
    fill(rest(p => p < 0));    // 最低（最後尾）
    const winnerSet = new Set(winners);
    return {
      generated_at: Date.now(), mode: 'raffle',
      pairs:  winners.map(gid => ({ guest_id: gid, cast_id: null, match_type: (guests[gid]?.priority ?? 0) >= 1 ? 'priority' : 'raffle' })),
      losers: allIds.filter(id => !winnerSet.has(id)),
    };
  }

  // 部制対応: 部ごとに定員分抽選し前の部に詰める
  const perSession = capacity > 0 ? capacity : allIds.length;
  const allPairs   = [];
  const globalWon  = new Set();

  for (let ses = 1; ses <= sessions; ses++) {
    const remaining = allIds.filter(id => !globalWon.has(id));
    if (remaining.length === 0) break;
    const limit = Math.min(perSession, remaining.length);
    const group  = (p) => shuffle(remaining.filter(id => (guests[id]?.priority ?? 0) === p));
    const sesWinners = [];
    const fill = (pool) => {
      const slots = limit - sesWinners.length;
      if (slots <= 0 || pool.length === 0) return;
      sesWinners.push(...pool.slice(0, slots));
    };
    const rest = (pred) => shuffle(remaining.filter(id => !sesWinners.includes(id) && pred(guests[id]?.priority ?? 0)));
    fill(group(1)); fill(group(2)); fill(group(3));
    fill(rest(p => p >= 0));   // 通常
    fill(rest(p => p < 0));    // 最低（最後尾）
    sesWinners.forEach(gid => {
      globalWon.add(gid);
      allPairs.push({ guest_id: gid, session: ses, cast_id: null, match_type: (guests[gid]?.priority ?? 0) >= 1 ? 'priority' : 'raffle' });
    });
  }

  return {
    generated_at: Date.now(), mode: 'raffle',
    pairs:  allPairs,
    losers: allIds.filter(id => !globalWon.has(id)),
  };
};

/**
 * @param {Object} casts
 * @param {Object} guests
 * @param {string} mode             - 'cast_select' | 'guest_select' | 'both_select' | 'random_only'
 * @param {number|null} sessions    - 部数 (null = 部制なし)
 * @param {Object|null} sessionAttToday - { [castId]: { [ses]: 'working'|'absent' } } (今日分)
 * @returns {{ generated_at, mode, pairs }}
 */
export const runMatching = (casts, guests, mode, sessions, sessionAttToday) => {
  if (!casts || !guests) return { generated_at: Date.now(), mode, pairs: [] };

  // 部制あり: session_attendance に working がある、または未設定のキャストを対象とする
  // 部制なし: cast.status === 'working' で判定
  const workingCasts = Object.fromEntries(
    Object.entries(casts).filter(([castId, cast]) => {
      if (sessions) {
        const att = (sessionAttToday || {})[castId] || {};
        return Object.values(att).some(s => s === 'working');
      }
      return cast.status === 'working';
    })
  );

  let pairs;
  if (sessions && sessions >= 2) {
    if      (mode === 'guest_select') pairs = matchByGuestWithSessions(workingCasts, guests, sessionAttToday, sessions);
    else if (mode === 'both_select')  pairs = matchByBothWithSessions(workingCasts, guests, sessionAttToday, sessions);
    else if (mode === 'random_only')  pairs = matchByRandomWithSessions(workingCasts, guests, sessionAttToday, sessions);
    else                              pairs = matchByCastWithSessions(workingCasts, guests, sessionAttToday, sessions);
  } else {
    if      (mode === 'guest_select') pairs = matchByGuest(workingCasts, guests);
    else if (mode === 'both_select')  pairs = matchByBoth(workingCasts, guests);
    else if (mode === 'random_only')  pairs = matchByRandom(workingCasts, guests);
    else                              pairs = matchByCast(workingCasts, guests);
  }

  return {
    generated_at: Date.now(),
    mode,
    pairs: pairs.map((pair, i) => ({ ...pair, room_number: i + 1 })),
  };
};
